import { useId, useMemo } from "react";
import { StatusBadge } from "@/components/primitives";
import { useT } from "@/i18n/I18nProvider";
import { ResourceMapGraphList } from "./ResourceMapGraphList";
import { boundedGraph } from "./resource-map-bounded";
import "./resource-map.css";
import {
  RESOURCE_MAP_EDGE_PATTERNS,
  RESOURCE_MAP_VIEWBOX,
  RESOURCE_MAP_VISIBLE_EDGE_CAP,
  RESOURCE_MAP_VISIBLE_NODE_CAP,
  edgeGeometry,
  type ResourceMapGraphEdge,
  type ResourceMapGraphNode,
} from "./resource-map-layout";

/**
 * Resource Map 시각 미리보기 (ADR 0020 · #8 M4-I13, 결정 1·7)
 *
 * 서버 순서의 노드·간선에서 **고정 상한 안의 부분집합만** 결정적으로 배치해
 * 그립니다(배치 규칙은 resource-map-layout.ts). 힘-기반 레이아웃·타이머·난수·
 * 서드파티·원시 값·unsafe HTML은 없고, O(N+E)입니다. 이 미리보기는 접근성 표
 * (가상 스크롤 목록)를 대체하지 않습니다 — 목록이 정본이고, 미리보기는 상한에
 * 걸리면 생략 사실을 말합니다.
 *
 * 색은 상태를 전달하는 유일한 수단이 아닙니다: 노드 상태는 글리프+텍스트,
 * 간선 관계는 텍스트 라벨 + 대시 패턴, 방향은 간선 중간의 화살촉 모양입니다.
 * 노드 건강(#8 M4-I14)은 서버 값 그대로 StatusBadge(역할 토큰 색 + 글리프 +
 * 텍스트)로 그리고, 카드 테두리 색도 같은 값의 역할 토큰입니다. 정본 표가 같은
 * 값을 같은 배지로 보여줍니다.
 */
export {
  RESOURCE_MAP_EDGE_PATTERNS,
  RESOURCE_MAP_VISIBLE_EDGE_CAP,
  RESOURCE_MAP_VISIBLE_NODE_CAP,
  placeResourceMapNodes,
} from "./resource-map-layout";
export type { ResourceMapGraphEdge, ResourceMapGraphNode } from "./resource-map-layout";

export interface ResourceMapGraphProps {
  nodes: ReadonlyArray<ResourceMapGraphNode>;
  edges: ReadonlyArray<ResourceMapGraphEdge>;
  /** 해석된·UID 있는 노드를 선택했을 때만 호출됩니다(비활성 노드는 절대 호출되지 않음). */
  onSelect: (node: ResourceMapGraphNode) => void;
  /** 접근성 이름/설명의 문맥(예: focus 자원 표기). */
  title: string;
}

/** 노드 상태 어휘의 메시지 ID. 템플릿으로 만들지 않아야 게이트가 정적으로 셉니다. */
function nodeStateId(node: ResourceMapGraphNode): string {
  if (node.focus) return "resourcemap.graph.state.focus";
  if (!node.resolved) return "resourcemap.graph.state.unresolved";
  return "resourcemap.graph.state.resolved";
}

/** useId는 콜론을 포함할 수 있어 url(#…) 참조에 안전한 문자만 남깁니다. */
function safeIdSuffix(raw: string): string {
  return raw.replace(/[^a-zA-Z0-9_-]/g, "");
}

export function ResourceMapGraph({ nodes, edges, onSelect, title }: ResourceMapGraphProps) {
  const { t } = useT("resourcemap");
  const instance = safeIdSuffix(useId());
  const arrowId = `resource-map-arrow-${instance}`;
  const labelledBy = `resource-map-graph-title-${instance}`;
  const describedBy = `resource-map-graph-desc-${instance}`;
  /* 그림과 텍스트 대체가 **같은 부분집합**을 봅니다. 각자 계산하면 두 표현이
     다른 것을 말하게 되고, 그때 대체는 대체가 아닙니다. (ADR 0025) */
  const graph = useMemo(() => boundedGraph(nodes, edges), [nodes, edges]);
  const { placed, byKey, edges: visibleEdges, omittedNodes, omittedEdges } = graph;
  const relationTypes = useMemo(() => {
    const seen = new Set<string>();
    for (const e of visibleEdges) seen.add(e.type);
    return Array.from(seen).sort();
  }, [visibleEdges]);

  return (
    <figure className="resource-map-graph" data-testid="resource-map-graph">
      <figcaption id={labelledBy} className="resource-map-graph__title">
        {t("resourcemap.graph.caption", {
          title,
          nodes: placed.length,
          totalNodes: nodes.length,
          edges: visibleEdges.length,
          totalEdges: edges.length,
        })}
      </figcaption>
      <p id={describedBy} className="resource-map-graph__desc" data-testid="resource-map-graph-desc">
        {t("resourcemap.graph.description", {
          nodeCap: RESOURCE_MAP_VISIBLE_NODE_CAP,
          edgeCap: RESOURCE_MAP_VISIBLE_EDGE_CAP,
        })}
      </p>
      {(omittedNodes > 0 || omittedEdges > 0) && (
        <p className="resource-map-graph__notice" role="status">
          {t("resourcemap.graph.notice", {
            nodes: omittedNodes,
            totalNodes: nodes.length,
            edges: omittedEdges,
            totalEdges: edges.length,
          })}
        </p>
      )}
      <div className="resource-map-graph__canvas">
        <svg
          className="resource-map-graph__svg"
          viewBox={`0 0 ${RESOURCE_MAP_VIEWBOX} ${RESOURCE_MAP_VIEWBOX}`}
          role="img"
          aria-labelledby={labelledBy}
          aria-describedby={describedBy}
        >
          <defs>
            {/* 방향 화살촉 — 색이 아니라 모양으로 from → to를 말합니다. 간선
                **중간 꼭짓점**(marker-mid)에 붙어 노드 오버레이 밖에서 보입니다.
                id는 인스턴스별입니다(같은 문서의 미리보기 둘이 충돌하지 않음). */}
            <marker
              id={arrowId}
              viewBox="0 0 10 10"
              refX="5"
              refY="5"
              markerWidth="8"
              markerHeight="8"
              orient="auto"
              markerUnits="userSpaceOnUse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" className="resource-map-graph__arrow" />
            </marker>
          </defs>
          {visibleEdges.map((e, i) => {
            const a = byKey.get(e.from);
            const b = byKey.get(e.to);
            if (!a || !b) return null;
            const { arrow, label } = edgeGeometry(a, b);
            return (
              <g
                key={`${e.from}→${e.to}→${e.type}→${i}`}
                className={`resource-map-graph__edge${e.resolved ? "" : " resource-map-graph__edge--unresolved"}`}
                data-relation={e.type}
                data-from={e.from}
                data-to={e.to}
              >
                <polyline
                  points={`${a.x},${a.y} ${arrow.x},${arrow.y} ${b.x},${b.y}`}
                  fill="none"
                  markerMid={`url(#${arrowId})`}
                  strokeDasharray={RESOURCE_MAP_EDGE_PATTERNS[e.type] ?? "5 5"}
                />
                <text x={label.x} y={label.y} className="resource-map-graph__edge-label">
                  {e.type} →{e.resolved ? "" : " (unresolved)"}
                </text>
              </g>
            );
          })}
        </svg>
        <ul className="resource-map-graph__nodes" aria-label={t("resourcemap.graph.nodesLabel")}>
          {placed.map(({ node, x, y }) => {
            const selectable = node.resolved && node.uid !== "" && node.resource !== "";
            return (
              <li
                key={node.key}
                className="resource-map-graph__node-slot"
                style={{ left: `${(x / RESOURCE_MAP_VIEWBOX) * 100}%`, top: `${(y / RESOURCE_MAP_VIEWBOX) * 100}%` }}
              >
                <button
                  type="button"
                  className={`resource-map-graph__node${node.focus ? " resource-map-graph__node--focus" : ""}${
                    node.resolved ? "" : " resource-map-graph__node--unresolved"
                  }`}
                  disabled={!selectable}
                  aria-disabled={!selectable}
                  aria-current={node.focus ? "location" : undefined}
                  data-health={node.health}
                  data-testid={`resource-map-graph-node-${node.key}`}
                  onClick={() => {
                    if (selectable) onSelect(node);
                  }}
                >
                  <span className="resource-map-graph__node-state">{t(nodeStateId(node))}</span>
                  <StatusBadge severity={node.health} small />
                  <span className="resource-map-graph__node-kind">{node.kind}</span>
                  <span className="resource-map-graph__node-name">
                    {node.namespace ? `${node.namespace}/` : ""}
                    {node.name}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      <ul className="resource-map-graph__legend" aria-label={t("resourcemap.graph.legendLabel")}>
        {relationTypes.map((type) => (
          <li key={type} className="resource-map-graph__legend-item">
            <svg className="resource-map-graph__legend-swatch" viewBox="0 0 48 8" aria-hidden="true" focusable="false">
              <line x1={0} y1={4} x2={48} y2={4} strokeDasharray={RESOURCE_MAP_EDGE_PATTERNS[type] ?? "5 5"} />
            </svg>
            <span>{type}</span>
          </li>
        ))}
        <li className="resource-map-graph__legend-item">
          <span className="resource-map-graph__node-state">{t("resourcemap.graph.state.focus")}</span>
          <span className="resource-map-graph__node-state">{t("resourcemap.graph.state.resolved")}</span>
          <span className="resource-map-graph__node-state">
            {t("resourcemap.graph.state.unresolved")} ({t("resourcemap.graph.legend.unselectable")})
          </span>
        </li>
        <li className="resource-map-graph__legend-item" data-testid="resource-map-graph-legend-health">
          <span>{t("resourcemap.graph.legend.health")}</span>
          <StatusBadge severity="healthy" small />
          <StatusBadge severity="warning" small />
          <StatusBadge severity="critical" small />
          <StatusBadge severity="unknown" small />
        </li>
        <li className="resource-map-graph__legend-item" data-testid="resource-map-graph-legend-direction">
          {/* 자립 화살촉 모양 — 다른 SVG의 marker를 참조하지 않습니다. */}
          <svg className="resource-map-graph__legend-swatch" viewBox="0 0 48 8" aria-hidden="true" focusable="false">
            <line x1={0} y1={4} x2={40} y2={4} />
            <polygon points="38,0 48,4 38,8" className="resource-map-graph__arrow" />
          </svg>
          <span>{t("resourcemap.graph.legend.direction")}</span>
        </li>
      </ul>
      {/* 그림을 볼 수 없는 사용자를 위한 **같은 데이터의** 키보드 목록입니다. */}
      <ResourceMapGraphList graph={graph} totalNodes={nodes.length} totalEdges={edges.length} onSelect={onSelect} />
    </figure>
  );
}
